import { DeductionDetailJson } from "@/api"
import { LoadingSpinner } from "@/app/loading.tsx"
import { UserState } from "@/auth/user"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { currencyFormatter } from "@/utils"
import { format_ai_confidence_percent } from "@/utils/format_ai_confidence_percent"
import {
  AlertTriangle,
  Brain,
  CheckCircle,
  ChevronRight,
  DollarSign,
  MessageSquare,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
  Wand2,
} from "lucide-react"
import { VNode } from "preact"
import { useState, useEffect } from "preact/hooks"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer"
import { is_unfi_portal_thread } from "./RightPane/Conversation"
import { PromotionalContextSelector, PromotionalContextSelectionData } from "./PromotionalContextSelector"

function shouldReplyToDispute(source: string, existingDisputeType?: string, messages: { source: string }[] = []): boolean {
  // UNFI's Dispute Center takes no replies; the File Dispute drawer appeals a denied claim.
  if (is_unfi_portal_thread(messages)) {
    return false
  }
  // If the existing dispute is a backup request, don't reply - open file dispute dialog instead
  if (source === "kehe") {
    // we can never double file disputes with kehe
    return true
  }
  // only reply to chargebacks. everything else should file new dispute for.
  return existingDisputeType === "chargeback"
}

interface TriageResult {
  is_disputable: boolean
  dispute_amount?: number
  confidence: number
  reasoning: string
  dispute_message?: string
  evidence: string[]
  pdf_analysis?: string
}

interface TriageResponse {
  result: TriageResult
  rating?: TriageRating
}

type TriageRating = "thumbs_up" | "thumbs_down" | "perfect"

interface UseWizardOptions {
  deduction: DeductionDetailJson
  autoOpen?: boolean
}

export interface UseWizardResult {
  trigger: () => void
  element: VNode | null
}

export function useWizard({ deduction, autoOpen = false }: UseWizardOptions): UseWizardResult {
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<TriageResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDialog, setShowDialog] = useState(false)
  const [rating, setRating] = useState<TriageRating | null>(null)
  const [ratingLoading, setRatingLoading] = useState(false)
  const [reTriageLoading, setReTriageLoading] = useState(false)
  const [pendingAnalysis, setPendingAnalysis] = useState(false)
  const [showDisclaimer, setShowDisclaimer] = useState(true)
  const [showPromoSelector, setShowPromoSelector] = useState(false)
  const [promotionalContext, setPromotionalContext] = useState<PromotionalContextSelectionData | null>(null)
  const [hasAutoOpened, setHasAutoOpened] = useState(false)
  const user = UserState.use()

  // Check if user has opted to not show the disclaimer again
  useEffect(() => {
    const dontShowDisclaimer = localStorage.getItem("wizard-disclaimer-dismissed")
    if (dontShowDisclaimer === "true") {
      setShowDisclaimer(false)
    }
  }, [])

  // Auto-open wizard if autoOpen prop is true
  useEffect(() => {
    if (autoOpen && !hasAutoOpened && !showDialog) {
      setHasAutoOpened(true)
      setShowDialog(true)
      const dontShowDisclaimer = localStorage.getItem("wizard-disclaimer-dismissed")
      if (dontShowDisclaimer === "true") {
        setPendingAnalysis(false)
        setTimeout(() => {
          startAnalysis()
        }, 100)
      } else {
        setPendingAnalysis(true)
      }
    }
  }, [autoOpen, hasAutoOpened, showDialog])

  function handleDontShowAgain() {
    localStorage.setItem("wizard-disclaimer-dismissed", "true")
    setShowDisclaimer(false)
    startAnalysis()
  }

  function handlePromotionalContextSubmit(data: PromotionalContextSelectionData) {
    setPromotionalContext(data)
    setShowPromoSelector(false)
    setShowDialog(true)
    if (!showDisclaimer) {
      startAnalysis(data)
    } else {
      setPendingAnalysis(true)
    }
  }

  function trigger() {
    setError(null)
    setResponse(null)
    setRating(null)

    const isRetailerPromotion = deduction.category?.toLowerCase().includes("retailer promotion")
    if (isRetailerPromotion && !promotionalContext && !deduction.promotion_id) {
      setShowPromoSelector(true)
      return
    }

    setShowDialog(true)
    if (!showDisclaimer) {
      startAnalysis()
    } else {
      setPendingAnalysis(true)
    }
  }

  async function startAnalysis(overridePromotionalContext?: PromotionalContextSelectionData) {
    setLoading(true)
    setError(null)
    setResponse(null)
    setRating(null)
    setPendingAnalysis(false)

    try {
      const requestBody: any = { feedback: null }

      // Use override promotional context if provided, otherwise use state
      const contextToUse = overridePromotionalContext || promotionalContext

      // Add promotional context if available
      if (contextToUse) {
        requestBody.promotional_context = contextToUse
      }

      const response = await fetch(`/api/deduction/${deduction.id}/triage/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Wizard analysis failed: ${response.statusText}`)
      }

      // Use SSE to stream the response
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error("No response body")
      }

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split("\n")

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6)
            try {
              const event = JSON.parse(data)

              if (event.type === "progress") {
                // Progress events are handled by the loading state
                console.log(`Progress: ${event.step} - ${event.message}`)
              } else if (event.type === "result") {
                setResponse({
                  result: event.result,
                  rating: event.rating,
                })
                if (event.rating) {
                  setRating(event.rating)
                }
              } else if (event.type === "error") {
                setError(event.error)
              }
            } catch (e) {
              console.error("Failed to parse SSE event:", e)
            }
          }
        }
      }
    } catch (err) {
      console.error("Wizard analysis failed:", err)
      setError(err instanceof Error ? err.message : "Unknown error occurred")
    } finally {
      setLoading(false)
    }
  }

  async function handleReTriage(feedback: string) {
    setReTriageLoading(true)
    setError(null)

    try {
      const requestBody: any = { feedback }

      // Add promotional context if available
      if (promotionalContext) {
        requestBody.promotional_context = promotionalContext
      }

      const response = await fetch(`/api/deduction/${deduction.id}/triage/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Wizard re-analysis failed: ${response.statusText}`)
      }

      // Use SSE to stream the response
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error("No response body")
      }

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split("\n")

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6)
            try {
              const event = JSON.parse(data)

              if (event.type === "progress") {
                console.log(`Progress: ${event.step} - ${event.message}`)
              } else if (event.type === "result") {
                setResponse({
                  result: event.result,
                  rating: event.rating,
                })
                setRating(null) // Reset rating for new analysis

                toast({
                  title: "Analysis updated",
                  description: "The Wizard has reconsidered the deduction based on your feedback.",
                })
              } else if (event.type === "error") {
                setError(event.error)
                toast({
                  title: "Re-analysis failed",
                  description: event.error,
                  variant: "destructive",
                })
              }
            } catch (e) {
              console.error("Failed to parse SSE event:", e)
            }
          }
        }
      }
    } catch (err) {
      console.error("Wizard re-analysis failed:", err)
      setError(err instanceof Error ? err.message : "Unknown error occurred")
      toast({
        title: "Re-analysis failed",
        description: err instanceof Error ? err.message : "Unknown error occurred",
        variant: "destructive",
      })
    } finally {
      setReTriageLoading(false)
    }
  }

  async function handleRating(selectedRating: TriageRating) {
    if (ratingLoading) return

    setRatingLoading(true)
    try {
      const response = await fetch(`/api/deduction/${deduction.id}/triage/rate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ rating: selectedRating }),
      })

      if (!response.ok) {
        throw new Error(`Rating failed: ${response.statusText}`)
      }

      setRating(selectedRating)
      toast({
        title: "Rating submitted",
        description: "Thank you for your feedback!",
      })
    } catch (err) {
      console.error("Rating failed:", err)
      toast({
        title: "Rating failed",
        description: err instanceof Error ? err.message : "Unknown error occurred",
        variant: "destructive",
      })
    } finally {
      setRatingLoading(false)
    }
  }

  function closeDialog() {
    setShowDialog(false)
    setResponse(null)
    setError(null)
    setRating(null)
    setPendingAnalysis(false)
  }

  if (!user) {
    return { trigger: () => {}, element: null }
  }

  const element = (
    <>
      {showPromoSelector && (
        <PromotionalContextSelector
          open={showPromoSelector}
          onClose={() => setShowPromoSelector(false)}
          onSubmit={handlePromotionalContextSubmit}
          deduction={deduction}
        />
      )}
      {showDialog && (
        <TriageDialog
          loading={loading}
          error={error}
          response={response}
          deduction={deduction}
          onRate={handleRating}
          currentRating={rating}
          ratingLoading={ratingLoading}
          onReTriage={handleReTriage}
          reTriageLoading={reTriageLoading}
          onClose={closeDialog}
          onStartAnalysis={() => startAnalysis(promotionalContext || undefined)}
          pendingAnalysis={pendingAnalysis}
          showDisclaimer={showDisclaimer}
          onDontShowAgain={handleDontShowAgain}
        />
      )}
    </>
  )

  return { trigger, element }
}

interface TriageDialogProps {
  loading: boolean
  error: string | null
  response: TriageResponse | null
  deduction: DeductionDetailJson
  onRate: (rating: TriageRating) => void
  currentRating: TriageRating | null
  ratingLoading: boolean
  onReTriage: (feedback: string) => void
  reTriageLoading: boolean
  onClose: () => void
  onStartAnalysis: () => void
  pendingAnalysis: boolean
  showDisclaimer: boolean
  onDontShowAgain: () => void
}

function TriageDialog({
  loading,
  error,
  response,
  deduction,
  onRate,
  currentRating,
  ratingLoading,
  onReTriage,
  reTriageLoading,
  onClose,
  onStartAnalysis,
  pendingAnalysis,
  showDisclaimer,
  onDontShowAgain,
}: TriageDialogProps) {
  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-transparent z-40" onClick={onClose} />
      {/* Sidebar */}
      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[500px] max-w-full bg-white shadow-2xl z-50 overflow-y-auto transition-transform duration-300 transform translate-x-0"
        style={{ boxShadow: "rgba(0,0,0,0.15) -4px 0px 24px" }}>
        {/* Header */}
        <TriageDialogHeader onClose={onClose} />

        {/* Content */}
        <div className="p-6">
          {showDisclaimer && pendingAnalysis && !loading && !response && !error && (
            <TriageDisclaimer onStart={onStartAnalysis} onDontShowAgain={onDontShowAgain} />
          )}
          {loading && <TriageLoadingState />}
          {reTriageLoading && <TriageReAnalyzingState />}
          {error && <TriageErrorState error={error} />}
          {response && !loading && !reTriageLoading && (
            <TriageResultDisplay
              result={response.result}
              deduction={deduction}
              onRate={onRate}
              currentRating={currentRating}
              ratingLoading={ratingLoading}
              onReTriage={onReTriage}
              reTriageLoading={reTriageLoading}
              onClose={onClose}
            />
          )}
        </div>

        {/* Footer */}
        <TriageDialogFooter onClose={onClose} />
      </div>
    </>
  )
}

function TriageDialogHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-center justify-between p-6 border-b bg-white sticky top-0 z-10">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <Wand2 className="h-5 w-5 text-purple-600" />
        Wizard Analysis
      </h2>
      <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

function TriageLoadingState() {
  const analysisSteps = [
    {
      title: "Gathering deduction data",
      description: "Collecting invoice details, files, and supporting documentation...",
      icon: "📋",
    },
    {
      title: "Processing PDF files",
      description: "Extracting and analyzing information from attached documents...",
      icon: "📄",
    },
    {
      title: "Analyzing context",
      description: "Reviewing notes, messages, and backup data for complete picture...",
      icon: "🔍",
    },
    {
      title: "Comparing with winning cases",
      description: "Cross-referencing with successful dispute examples and patterns...",
      icon: "🏆",
    },
    {
      title: "Generating analysis",
      description: "Creating comprehensive reasoning and dispute recommendations...",
      icon: "✨",
    },
    {
      title: "Finalizing results",
      description: "Preparing your analysis with confidence scores and evidence...",
      icon: "📊",
    },
  ]

  return <DynamicLoadingState steps={analysisSteps} />
}

function TriageReAnalyzingState() {
  const analysisSteps = [
    {
      title: "Processing your feedback",
      description: "Analyzing your corrections and understanding what needs to be reconsidered...",
      icon: "💭",
    },
    {
      title: "Reviewing deduction context",
      description: "Re-examining the invoice details, files, and supporting documentation...",
      icon: "📄",
    },
    {
      title: "Analyzing PDF files",
      description: "Extracting and processing information from attached documents...",
      icon: "🔍",
    },
    {
      title: "Comparing with winning cases",
      description: "Cross-referencing with successful dispute examples and patterns...",
      icon: "🏆",
    },
    {
      title: "Generating revised analysis",
      description: "Creating updated reasoning and dispute recommendations...",
      icon: "✨",
    },
    {
      title: "Finalizing results",
      description: "Preparing your updated analysis with confidence scores...",
      icon: "📊",
    },
  ]

  return <DynamicLoadingState steps={analysisSteps} />
}

interface LoadingStep {
  title: string
  description: string
  icon: string
}

interface DynamicLoadingStateProps {
  steps: LoadingStep[]
}

function DynamicLoadingState({ steps }: DynamicLoadingStateProps) {
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length)
    }, 5000) // Change step every 5 seconds

    return () => clearInterval(interval)
  }, [steps.length])

  const currentStepData = steps[currentStep]

  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-6 flex justify-center">
          <LoadingSpinner />
        </div>

        <div className="mb-6">
          <span className="text-4xl">{currentStepData.icon}</span>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">{currentStepData.title}</h3>

        <p className="text-gray-600 mb-6">{currentStepData.description}</p>

        {/* Progress indicator */}
        <div className="flex justify-center space-x-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index === currentStep ? "bg-blue-500" : "bg-gray-300"
              }`}
            />
          ))}
        </div>

        <p className="text-sm text-gray-500 mt-4">This usually takes about 30 seconds...</p>
      </div>
    </div>
  )
}

function TriageErrorState({ error }: { error: string }) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
      <p className="text-red-700 text-sm">{error}</p>
    </div>
  )
}

function TriageDialogFooter({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-lg">
      <Button onClick={onClose} variant="outline">
        Close
      </Button>
    </div>
  )
}

interface TriageDisclaimerProps {
  onStart: () => void
  onDontShowAgain: () => void
}

function TriageDisclaimer({ onStart, onDontShowAgain }: TriageDisclaimerProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false)

  function handleStart() {
    if (dontShowAgain) {
      onDontShowAgain()
    } else {
      onStart()
    }
  }

  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-6 flex justify-center">
          <div className="p-4 bg-yellow-100 rounded-full">
            <AlertTriangle className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Analysis Disclaimer</h3>

        <div className="space-y-3 text-sm text-gray-600 mb-6">
          <p>The Wizard uses AI to analyze your deduction and provide recommendations.</p>
          <p className="font-medium text-orange-800">
            ⚠️ The Wizard can make mistakes, so please double-check all recommendations before taking action.
          </p>
          <p>
            This analysis is meant to assist you, but you should always verify the information and use your professional
            judgment.
          </p>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-blue-900 text-sm">
              💡 <strong>Help us improve:</strong> After the analysis completes, you can provide feedback if the
              analysis is incorrect, and rate responses to help us improve the Wizard.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center mb-4">
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.currentTarget.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Don't show this disclaimer again</span>
          </label>
        </div>

        <Button onClick={handleStart} className="w-full" variant="default">
          I Understand - Start Analysis
        </Button>
      </div>
    </div>
  )
}

interface TriageResultDisplayProps {
  result: TriageResult
  deduction: DeductionDetailJson
  onRate: (rating: TriageRating) => void
  currentRating: TriageRating | null
  ratingLoading: boolean
  onReTriage: (feedback: string) => void
  reTriageLoading: boolean
  onClose: () => void
}

function TriageResultDisplay({
  result,
  deduction,
  onRate,
  currentRating,
  ratingLoading,
  onReTriage,
  reTriageLoading,
  onClose,
}: TriageResultDisplayProps) {
  return (
    <div className="space-y-6">
      <TriageResultHeader result={result} />
      <TriageKeyInfoGrid result={result} />
      <TriageDisputeMessage result={result} deduction={deduction} onClose={onClose} />
      <TriageReasoning result={result} />
      <TriageRatingSection onRate={onRate} currentRating={currentRating} ratingLoading={ratingLoading} />
      <TriageFeedbackSection result={result} onReTriage={onReTriage} reTriageLoading={reTriageLoading} />
      <TriageEvidence result={result} />
      <TriagePdfAnalysis result={result} />
    </div>
  )
}

function TriageResultHeader({ result }: { result: TriageResult }) {
  const confidenceColor =
    result.confidence > 0.8 ? "text-green-600" : result.confidence > 0.6 ? "text-yellow-600" : "text-red-600"

  const confidenceText = result.confidence > 0.8 ? "High" : result.confidence > 0.6 ? "Medium" : "Low"

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        {result.is_disputable ? (
          <AlertTriangle className="h-6 w-6 text-orange-500" />
        ) : (
          <CheckCircle className="h-6 w-6 text-green-500" />
        )}
        <div>
          <h3 className="text-lg font-semibold">{result.is_disputable ? "Disputable" : "Not Disputable"}</h3>
          <p className="text-sm text-gray-600">
            Confidence:{" "}
            <span className={confidenceColor}>
              {confidenceText} ({format_ai_confidence_percent(result.confidence)})
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

function TriageRatingSection({
  onRate,
  currentRating,
  ratingLoading,
}: {
  onRate: (rating: TriageRating) => void
  currentRating: TriageRating | null
  ratingLoading: boolean
}) {
  return (
    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
      <h4 className="font-semibold mb-3 text-gray-900">Rate this analysis</h4>
      <p className="text-sm text-gray-600 mb-4">Help us improve by rating how helpful this analysis was:</p>
      <div className="flex items-center gap-3">
        <Button
          variant={currentRating === "thumbs_up" ? "default" : "outline"}
          onClick={() => onRate("thumbs_up")}
          disabled={ratingLoading}
          className="flex items-center gap-2">
          <ThumbsUp className="h-4 w-4" />
          Helpful
        </Button>
        <Button
          variant={currentRating === "thumbs_down" ? "default" : "outline"}
          onClick={() => onRate("thumbs_down")}
          disabled={ratingLoading}
          className="flex items-center gap-2">
          <ThumbsDown className="h-4 w-4" />
          Not Helpful
        </Button>
        <Button
          variant={currentRating === "perfect" ? "default" : "outline"}
          onClick={() => onRate("perfect")}
          disabled={ratingLoading}
          className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          Perfect
        </Button>
      </div>
    </div>
  )
}

/** The three things users actually do when they re-run triage (from feedback analysis). */
type ReTriageIntent = "reread" | "correct" | "refine_message"

interface ReTriageIntentConfig {
  label: string
  blurb: string
  /** Free-text is required to submit (false for "re-read attachments"). */
  requiresText: boolean
  placeholder: string
  /** Builds the feedback string sent to the model. Tagged so re-runs are separable for eval. */
  compose: (text: string) => string
}

const RE_TRIAGE_INTENTS: Record<ReTriageIntent, ReTriageIntentConfig> = {
  reread: {
    label: "Re-read new attachments / notes",
    blurb: "You've added files or internal notes since this analysis — have the Wizard read them and re-analyze.",
    requiresText: false,
    placeholder: "Optional: point to a specific file or note to focus on…",
    compose: (text: string) =>
      `[Re-read attachments] New documents and/or internal notes were added since the last analysis. Re-read ALL attached files and internal notes and re-analyze from scratch.${
        text ? `\nFocus: ${text}` : ""
      }`,
  },
  correct: {
    label: "Correct the analysis",
    blurb: "The assessment, amount, or reasoning is wrong — tell the Wizard what to fix.",
    requiresText: true,
    placeholder:
      "What did the Wizard get wrong, and why? Be specific. For example:\n" +
      "• \"This IS disputable — the signed BOL shows we shipped in full.\"\n" +
      "• \"The dispute amount should be $326.70 (only the price variance), not the full $1,200.\"\n" +
      "• \"This was already repaid on check #4480918, so it's not disputable.\"\n" +
      "• \"We invoiced only the shipped quantity, so the short-ship deduction is invalid.\"",
    compose: (text: string) => `[Correct analysis] ${text}`,
  },
  refine_message: {
    label: "Refine the dispute message",
    blurb: "Keep the assessment but rewrite the outgoing dispute letter.",
    requiresText: true,
    placeholder: "How should the dispute message read? Tone, facts to include, what to drop…",
    compose: (text: string) =>
      `[Refine dispute message] Keep your disputability assessment and dispute amount. Rewrite ONLY the dispute_message per this guidance: ${text}`,
  },
}

const RE_TRIAGE_INTENT_ORDER: ReTriageIntent[] = ["reread", "correct", "refine_message"]

function TriageFeedbackSection({
  result,
  onReTriage,
  reTriageLoading,
}: {
  result: TriageResult
  onReTriage: (feedback: string) => void
  reTriageLoading: boolean
}) {
  const [intent, setIntent] = useState<ReTriageIntent | null>(null)
  const [feedback, setFeedback] = useState("")

  // "Refine the dispute message" only makes sense when there's a dispute to refine.
  const available_intents = RE_TRIAGE_INTENT_ORDER.filter(
    (key: ReTriageIntent) => key !== "refine_message" || result.is_disputable
  )

  function reset() {
    setIntent(null)
    setFeedback("")
  }

  function handleSubmit() {
    if (!intent) return
    const config = RE_TRIAGE_INTENTS[intent]
    const text = feedback.trim()
    if (config.requiresText && !text) return
    onReTriage(config.compose(text))
    reset()
  }

  if (!intent) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <h4 className="font-semibold mb-3 text-gray-900">Re-analyze this deduction</h4>
        <p className="text-sm text-gray-600 mb-4">What would you like the Wizard to do?</p>
        <div className="space-y-2">
          {available_intents.map((key: ReTriageIntent) => (
            <button
              key={key}
              type="button"
              onClick={() => setIntent(key)}
              className="group flex w-full items-center gap-3 rounded-md border border-gray-300 bg-white p-3 text-left cursor-pointer hover:border-purple-400 hover:bg-purple-50 hover:shadow-sm active:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 transition-all">
              <div className="flex-1">
                <div className="font-medium text-gray-900 text-sm group-hover:text-purple-700">
                  {RE_TRIAGE_INTENTS[key].label}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">{RE_TRIAGE_INTENTS[key].blurb}</div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-purple-500" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  const config = RE_TRIAGE_INTENTS[intent]
  return (
    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
      <h4 className="font-semibold mb-1 text-gray-900">{config.label}</h4>
      <p className="text-sm text-gray-600 mb-3">{config.blurb}</p>

      <textarea
        value={feedback}
        onChange={e => setFeedback(e.currentTarget.value)}
        placeholder={config.placeholder}
        className="w-full p-3 border border-gray-300 rounded-md resize-none h-40 mb-4"
        disabled={reTriageLoading}
      />
      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={reTriageLoading || (config.requiresText && !feedback.trim())}
          className="flex-1">
          {intent === "reread" ? "Re-read & re-analyze" : "Submit & re-analyze"}
        </Button>
        <Button onClick={reset} variant="outline" disabled={reTriageLoading}>
          Back
        </Button>
      </div>
    </div>
  )
}

function TriageKeyInfoGrid({ result }: { result: TriageResult }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {result.dispute_amount && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
          <DollarSign className="h-5 w-5 text-blue-600" />
          <div>
            <p className="font-medium text-blue-900">Recommended Dispute Amount</p>
            <p className="text-lg font-semibold text-blue-600">{currencyFormatter(result.dispute_amount)}</p>
          </div>
        </div>
      )}
    </div>
  )
}

function logDisputeIntention(deductionId: string) {
  fetch(`/api/deduction/${deductionId}/triage/track-dispute`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
  }).catch(err => {
    console.error("Failed to track intention to dispute:", err)
  })
}

function TriageDisputeMessage({
  result,
  deduction,
  onClose,
}: {
  result: TriageResult
  deduction: DeductionDetailJson
  onClose: () => void
}) {
  if (!result.dispute_message) return null

  function handleFileDispute() {
    onClose() // Close the triage dialog

    // Track intention to dispute
    logDisputeIntention(deduction.id)

    // Check if we should reply to existing dispute or open new dispute drawer
    const shouldReply = shouldReplyToDispute(deduction.source, deduction.dispute?.type, deduction.messages)

    if (deduction.dispute && shouldReply) {
      // If already disputed and conditions are met, populate the AI-generated message in the conversation response field
      if (result.dispute_message) {
        // Find and populate the conversation textarea directly
        setTimeout(() => {
          const textareas = document.querySelectorAll('textarea[data-ai-message-target="true"]')
          textareas.forEach(textarea => {
            if (textarea instanceof HTMLTextAreaElement && !textarea.value) {
              textarea.value = result.dispute_message!
              // Trigger change event to update React state
              const event = new Event("input", { bubbles: true })
              textarea.dispatchEvent(event)

              // Scroll the textarea into view
              textarea.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
              })
            }
          })
        }, 100) // Small delay to ensure conversation components are rendered

        toast({
          title: "Message ready",
          description:
            "AI-generated message has been added to the conversation. You can review and edit it before sending.",
        })
      }
    } else {
      // If no dispute exists or conditions not met, open the dispute drawer as before
      DrawerDeduction.set(deduction)
    }
  }

  return (
    <div>
      <h4 className="font-semibold mb-3 text-gray-900 flex items-center gap-2">
        <MessageSquare className="h-5 w-5" />
        Suggested Dispute Message
      </h4>
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <p className="text-gray-700 whitespace-pre-wrap font-medium">{result.dispute_message}</p>
      </div>
      <div className="mt-3">
        <Button onClick={handleFileDispute} className="w-full" variant="default">
          {deduction.dispute && shouldReplyToDispute(deduction.source, deduction.dispute.type, deduction.messages)
            ? "Reply to Dispute with this message"
            : "File Dispute with this message"}
        </Button>
      </div>
    </div>
  )
}

function TriageReasoning({ result }: { result: TriageResult }) {
  return (
    <div>
      <h4 className="font-semibold mb-3 text-gray-900">Analysis Reasoning</h4>
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-700 leading-relaxed">{result.reasoning}</p>
      </div>
    </div>
  )
}

function TriageEvidence({ result }: { result: TriageResult }) {
  if (result.evidence.length === 0) return null

  return (
    <div>
      <h4 className="font-semibold mb-3 text-gray-900">Supporting Evidence</h4>
      <div className="space-y-2">
        {result.evidence.map((evidence, index) => (
          <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
            <span className="text-green-600 mt-1">•</span>
            <span className="text-gray-700">{evidence}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function TriagePdfAnalysis({ result }: { result: TriageResult }) {
  if (!result.pdf_analysis) return null

  return (
    <div>
      <h4 className="font-semibold mb-3 text-gray-900">PDF Analysis</h4>
      <div className="p-4 bg-blue-50 rounded-lg">
        <p className="text-gray-700 leading-relaxed">{result.pdf_analysis}</p>
      </div>
    </div>
  )
}
